import { useState, useEffect } from "react";
import { Save, RefreshCw, PoundSterling, Layers, Wrench, Zap, Bath, Flame, Percent, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPanel = ({ token }) => {
  const [pricing, setPricing] = useState(null);
  const [simplePricing, setSimplePricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("simple_quote");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPricing();
    fetchSimplePricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/owner/pricing/all`, { headers });
      setPricing(response.data);
    } catch (error) {
      toast.error("Failed to load pricing");
    } finally {
      setLoading(false);
    }
  };

  const fetchSimplePricing = async () => {
    try {
      const response = await axios.get(`${API}/owner/pricing/simple-quote`, { headers });
      setSimplePricing(response.data.settings);
    } catch (error) {
      console.error("Failed to load simple quote pricing");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/pricing/all`, pricing, { headers });
      toast.success("Pricing saved successfully - changes are now live on the site!");
    } catch (error) {
      toast.error("Failed to save pricing");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSimplePricing = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/pricing/simple-quote`, simplePricing, { headers });
      toast.success("Simple quote pricing saved!");
    } catch (error) {
      toast.error("Failed to save simple quote pricing");
    } finally {
      setSaving(false);
    }
  };

  const updateNestedValue = (path, value) => {
    const numValue = value === "" ? 0 : parseFloat(value) || 0;
    const newPricing = { ...pricing };
    const keys = path.split(".");
    let current = newPricing;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = numValue;
    setPricing(newPricing);
  };

  const tabs = [
    { id: "simple_quote", label: "Simple Quote", icon: Calculator },
    { id: "wall_tiling", label: "Wall Tiling", icon: Layers },
    { id: "floor_tiling", label: "Floor Tiling", icon: Layers },
    { id: "pattern_multipliers", label: "Patterns", icon: Percent },
    { id: "prep_work", label: "Prep Work", icon: Wrench },
    { id: "fixtures", label: "Fixtures", icon: Bath },
    { id: "electrical", label: "Electrical", icon: Zap },
    { id: "heating", label: "Heating", icon: Flame },
    { id: "removal", label: "Removal", icon: Wrench },
    { id: "plumbing", label: "Plumbing", icon: Wrench },
    { id: "labour", label: "Labour", icon: PoundSterling },
    { id: "margins", label: "Margins", icon: Percent }
  ];

  if (loading) {
    return (
      <div className="p-6 text-center">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
        <p className="text-gray-500 mt-2">Loading pricing...</p>
      </div>
    );
  }

  const renderPriceInput = (label, path, suffix = "£", isPercent = false) => {
    const keys = path.split(".");
    let value = pricing;
    for (const key of keys) {
      value = value?.[key];
    }
    
    return (
      <div className="space-y-1">
        <Label className="text-sm text-gray-600">{label}</Label>
        <div className="relative">
          {!isPercent && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{suffix}</span>}
          <Input
            type="number"
            value={value ?? 0}
            onChange={(e) => updateNestedValue(path, e.target.value)}
            className={!isPercent ? "pl-7" : ""}
          />
          {isPercent && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>}
        </div>
      </div>
    );
  };

  const renderRangeInput = (label, basePath) => {
    const minPath = `${basePath}.min`;
    const maxPath = `${basePath}.max`;
    const keys = basePath.split(".");
    let value = pricing;
    for (const key of keys) {
      value = value?.[key];
    }
    
    return (
      <div className="space-y-1">
        <Label className="text-sm text-gray-600">{label}</Label>
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
            <Input
              type="number"
              value={value?.min ?? 0}
              onChange={(e) => updateNestedValue(minPath, e.target.value)}
              className="pl-7"
              placeholder="Min"
            />
          </div>
          <span className="text-gray-400">-</span>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
            <Input
              type="number"
              value={value?.max ?? 0}
              onChange={(e) => updateNestedValue(maxPath, e.target.value)}
              className="pl-7"
              placeholder="Max"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "simple_quote":
        if (!simplePricing) {
          return <p className="text-gray-500">Loading simple quote pricing...</p>;
        }
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-blue-700">
                These settings control the Simple Quote estimator pricing. Adjust base prices and multipliers to generate accurate quick estimates.
              </p>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Base Prices by Room Size</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Small Room (Min - Max)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={simplePricing.base_prices?.small?.[0] || 3000}
                      onChange={(e) => setSimplePricing(prev => ({
                        ...prev,
                        base_prices: { ...prev.base_prices, small: [parseInt(e.target.value), prev.base_prices?.small?.[1] || 6000] }
                      }))}
                      placeholder="Min"
                    />
                    <Input
                      type="number"
                      value={simplePricing.base_prices?.small?.[1] || 6000}
                      onChange={(e) => setSimplePricing(prev => ({
                        ...prev,
                        base_prices: { ...prev.base_prices, small: [prev.base_prices?.small?.[0] || 3000, parseInt(e.target.value)] }
                      }))}
                      placeholder="Max"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Medium Room (Min - Max)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={simplePricing.base_prices?.medium?.[0] || 5000}
                      onChange={(e) => setSimplePricing(prev => ({
                        ...prev,
                        base_prices: { ...prev.base_prices, medium: [parseInt(e.target.value), prev.base_prices?.medium?.[1] || 10000] }
                      }))}
                      placeholder="Min"
                    />
                    <Input
                      type="number"
                      value={simplePricing.base_prices?.medium?.[1] || 10000}
                      onChange={(e) => setSimplePricing(prev => ({
                        ...prev,
                        base_prices: { ...prev.base_prices, medium: [prev.base_prices?.medium?.[0] || 5000, parseInt(e.target.value)] }
                      }))}
                      placeholder="Max"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Large Room (Min - Max)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={simplePricing.base_prices?.large?.[0] || 8000}
                      onChange={(e) => setSimplePricing(prev => ({
                        ...prev,
                        base_prices: { ...prev.base_prices, large: [parseInt(e.target.value), prev.base_prices?.large?.[1] || 16000] }
                      }))}
                      placeholder="Min"
                    />
                    <Input
                      type="number"
                      value={simplePricing.base_prices?.large?.[1] || 16000}
                      onChange={(e) => setSimplePricing(prev => ({
                        ...prev,
                        base_prices: { ...prev.base_prices, large: [prev.base_prices?.large?.[0] || 8000, parseInt(e.target.value)] }
                      }))}
                      placeholder="Max"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Multipliers</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Wall Tiling Multiplier</Label>
                  <Input
                    type="number"
                    step="0.05"
                    value={simplePricing.wall_tiling_multiplier || 1.25}
                    onChange={(e) => setSimplePricing(prev => ({
                      ...prev,
                      wall_tiling_multiplier: parseFloat(e.target.value)
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Partial Refit Multiplier</Label>
                  <Input
                    type="number"
                    step="0.05"
                    value={simplePricing.partial_refit_multiplier || 0.6}
                    onChange={(e) => setSimplePricing(prev => ({
                      ...prev,
                      partial_refit_multiplier: parseFloat(e.target.value)
                    }))}
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Floor Type Multipliers</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Tile</Label>
                  <Input
                    type="number"
                    step="0.05"
                    value={simplePricing.floor_type_multipliers?.tile || 1.0}
                    onChange={(e) => setSimplePricing(prev => ({
                      ...prev,
                      floor_type_multipliers: { ...prev.floor_type_multipliers, tile: parseFloat(e.target.value) }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vinyl</Label>
                  <Input
                    type="number"
                    step="0.05"
                    value={simplePricing.floor_type_multipliers?.vinyl || 0.75}
                    onChange={(e) => setSimplePricing(prev => ({
                      ...prev,
                      floor_type_multipliers: { ...prev.floor_type_multipliers, vinyl: parseFloat(e.target.value) }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Laminate</Label>
                  <Input
                    type="number"
                    step="0.05"
                    value={simplePricing.floor_type_multipliers?.laminate || 0.7}
                    onChange={(e) => setSimplePricing(prev => ({
                      ...prev,
                      floor_type_multipliers: { ...prev.floor_type_multipliers, laminate: parseFloat(e.target.value) }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Other</Label>
                  <Input
                    type="number"
                    step="0.05"
                    value={simplePricing.floor_type_multipliers?.other || 0.85}
                    onChange={(e) => setSimplePricing(prev => ({
                      ...prev,
                      floor_type_multipliers: { ...prev.floor_type_multipliers, other: parseFloat(e.target.value) }
                    }))}
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Electrical Work Costs</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Minimum Cost (£)</Label>
                  <Input
                    type="number"
                    value={simplePricing.electrical_costs?.min || 300}
                    onChange={(e) => setSimplePricing(prev => ({
                      ...prev,
                      electrical_costs: { ...prev.electrical_costs, min: parseInt(e.target.value) }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maximum Cost (£)</Label>
                  <Input
                    type="number"
                    value={simplePricing.electrical_costs?.max || 1200}
                    onChange={(e) => setSimplePricing(prev => ({
                      ...prev,
                      electrical_costs: { ...prev.electrical_costs, max: parseInt(e.target.value) }
                    }))}
                  />
                </div>
              </div>
            </div>
            
            <Button onClick={handleSaveSimplePricing} disabled={saving} className="bg-primary hover:bg-primary/90">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Simple Quote Pricing
            </Button>
          </div>
        );
      case "wall_tiling":
        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderPriceInput("Ceramic Tiles (per m²)", "wall_tiling.ceramic")}
            {renderPriceInput("Porcelain Tiles (per m²)", "wall_tiling.porcelain")}
            {renderPriceInput("Natural Stone (per m²)", "wall_tiling.natural_stone")}
            {renderPriceInput("Large Format (per m²)", "wall_tiling.large_format")}
            {renderPriceInput("Mosaic Tiles (per m²)", "wall_tiling.mosaic")}
          </div>
        );
      case "floor_tiling":
        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderPriceInput("Ceramic Tiles (per m²)", "floor_tiling.ceramic")}
            {renderPriceInput("Porcelain Tiles (per m²)", "floor_tiling.porcelain")}
            {renderPriceInput("Natural Stone (per m²)", "floor_tiling.natural_stone")}
            {renderPriceInput("Large Format (per m²)", "floor_tiling.large_format")}
            {renderPriceInput("Wood Effect (per m²)", "floor_tiling.wood_effect")}
          </div>
        );
      case "pattern_multipliers":
        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <p className="col-span-full text-sm text-gray-500 mb-2">
              Pattern complexity adds a percentage to the base tiling price
            </p>
            {renderPriceInput("Straight Layout", "pattern_multipliers.straight", "", true)}
            {renderPriceInput("Diagonal (+%)", "pattern_multipliers.diagonal", "", true)}
            {renderPriceInput("Herringbone (+%)", "pattern_multipliers.herringbone", "", true)}
            {renderPriceInput("Basket Weave (+%)", "pattern_multipliers.basket_weave", "", true)}
            {renderPriceInput("Chevron (+%)", "pattern_multipliers.chevron", "", true)}
            {renderPriceInput("Mosaic (+%)", "pattern_multipliers.mosaic", "", true)}
          </div>
        );
      case "prep_work":
        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderPriceInput("Tile Removal (per m²)", "prep_work.tile_removal")}
            {renderPriceInput("Self Levelling (per m²)", "prep_work.self_levelling")}
            {renderPriceInput("Backer Boards (per m²)", "prep_work.backer_boards")}
            {renderPriceInput("Waterproof Tanking (per m²)", "prep_work.waterproof_tanking")}
            {renderPriceInput("Subfloor Repair (per m²)", "prep_work.subfloor_repair")}
          </div>
        );
      case "fixtures":
        return (
          <div className="grid md:grid-cols-2 gap-4">
            {renderRangeInput("Standard Toilet Install", "fixtures.toilet_standard")}
            {renderRangeInput("Wall Hung Toilet", "fixtures.toilet_wall_hung")}
            {renderRangeInput("Standard Basin", "fixtures.basin_standard")}
            {renderRangeInput("Vanity Basin", "fixtures.basin_vanity")}
            {renderRangeInput("Standard Bath", "fixtures.bath_standard")}
            {renderRangeInput("Freestanding Bath", "fixtures.bath_freestanding")}
            {renderRangeInput("Shower Tray", "fixtures.shower_tray")}
            {renderRangeInput("Shower Enclosure", "fixtures.shower_enclosure")}
            {renderRangeInput("Walk-in Shower", "fixtures.walk_in_shower")}
          </div>
        );
      case "electrical":
        return (
          <div className="grid md:grid-cols-2 gap-4">
            {renderRangeInput("Extractor Fan", "electrical.extractor_fan")}
            {renderRangeInput("Humidity Extractor", "electrical.humidity_extractor")}
            {renderRangeInput("LED Mirror", "electrical.led_mirror")}
            {renderRangeInput("Mirror Cabinet", "electrical.mirror_cabinet")}
            {renderRangeInput("LED Lighting", "electrical.led_lighting")}
            {renderRangeInput("Shaver Socket", "electrical.shaver_socket")}
            {renderPriceInput("Underfloor Heating (per m²)", "electrical.underfloor_heating_sqm")}
          </div>
        );
      case "heating":
        return (
          <div className="grid md:grid-cols-2 gap-4">
            {renderRangeInput("Towel Rail (Plumbed)", "heating.towel_rail_plumbed")}
            {renderRangeInput("Towel Rail (Electric)", "heating.towel_rail_electric")}
            {renderPriceInput("Underfloor Heating (per m²)", "heating.underfloor_heating_sqm")}
          </div>
        );
      case "removal":
        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderPriceInput("Toilet Removal", "removal.toilet")}
            {renderPriceInput("Basin Removal", "removal.basin")}
            {renderPriceInput("Bath Removal", "removal.bath")}
            {renderPriceInput("Shower Removal", "removal.shower")}
            {renderPriceInput("Tile Removal (per m²)", "removal.tiles_sqm")}
            {renderPriceInput("Vanity Unit Removal", "removal.vanity_unit")}
          </div>
        );
      case "plumbing":
        return (
          <div className="grid md:grid-cols-2 gap-4">
            {renderRangeInput("Relocate Waste Pipe", "plumbing.relocate_waste")}
            {renderRangeInput("Relocate Water Supply", "plumbing.relocate_supply")}
            {renderRangeInput("New Isolation Valve", "plumbing.new_valve")}
            {renderRangeInput("Thermostatic Shower Valve", "plumbing.thermostatic_shower")}
          </div>
        );
      case "labour":
        return (
          <div className="grid md:grid-cols-2 gap-4">
            {renderPriceInput("Day Rate", "labour.day_rate")}
            {renderPriceInput("Hourly Rate", "labour.hourly_rate")}
          </div>
        );
      case "margins":
        return (
          <div className="grid md:grid-cols-2 gap-4">
            <p className="col-span-full text-sm text-gray-500 mb-2">
              These percentages are applied to calculate final estimates
            </p>
            {renderPriceInput("Material Markup", "margins.material_markup_percent", "", true)}
            {renderPriceInput("Contingency Buffer", "margins.contingency_percent", "", true)}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6" data-testid="pricing-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pricing Control Centre</h1>
          <p className="text-gray-600">Update prices across all estimator tools - changes go live immediately</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save All Changes"}
        </Button>
      </div>

      {/* Live Update Notice */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <p className="text-green-800 text-sm">
          <strong>Live Updates:</strong> All price changes will be reflected immediately on your website's estimator tools when you save.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-4">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? "bg-primary" : ""}
            >
              <Icon className="w-4 h-4 mr-1" />
              {tab.label}
            </Button>
          );
        })}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 capitalize">
          {tabs.find(t => t.id === activeTab)?.label} Pricing
        </h2>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default PricingPanel;
